import { useCallback, useEffect, useMemo } from 'react';
import { ProjectEdge } from '../../../../../../server/graphql/__generated.types';
import { useSuggestedProjectsQuery } from '../../../../Projects/operation.generated';
import useProjectCacheUtilities from '../../../../Projects/useProjectCacheUtilities';
import { ProjectFragment } from '../../../../fragments.generated';
import { useEventsContext } from '../../../../../context/events/EventsContext';

export interface usePaginatedProjectListResult {
  loading: boolean;
  error: any;
  projects: Array<ProjectFragment> | null;
  hasNextPage: boolean;
  loadMore: () => void;
}

export const DEFAULT_SUGGESTED_PROJECTS_PAGE_SIZE = 12;

/**
 * Fetch suggested projects
 * Backend pagination support available but inputs not utilized yet
 */
const usePaginatedSuggestedProjects = (): usePaginatedProjectListResult => {
  const { writeUserProjectQuery } = useProjectCacheUtilities();

  const { loading, error, data, fetchMore, networkStatus } =
    useSuggestedProjectsQuery({
      variables: {},
      nextFetchPolicy: 'cache-first',
      notifyOnNetworkStatusChange: true,
      onCompleted: d => {
        writeUserProjectQuery(
          d.user.id,
          d.user.suggestedProjects.edges as ProjectEdge[]
        );
      },
    });

  const hasNextPage =
    data?.user.suggestedProjects.pageInfo.hasNextPage || false;
  const endCursor = data?.user.suggestedProjects.pageInfo.endCursor;
  const loadMoreProjects = useCallback(() => {
    if (
      !loading &&
      !error &&
      hasNextPage &&
      endCursor &&
      !(networkStatus < 7)
    ) {
      fetchMore({
        variables: {},
      });
    }
  }, [loading, error, hasNextPage, endCursor, networkStatus, fetchMore]);
  const [eventTracking] = useEventsContext();

  useEffect(() => {
    if (data) {
      eventTracking.trackProjectsCount({
        projectsCount: data.user.suggestedProjects.edges.length,
      });
    }
  }, [eventTracking, data]);

  const projects = useMemo(() => {
    if (loading && !data) return null;
    if (
      data &&
      data.user &&
      data.user.suggestedProjects &&
      data.user.suggestedProjects.edges
    ) {
      return data.user.suggestedProjects.edges.length === 0
        ? []
        : data.user.suggestedProjects.edges.map(edge => edge.node);
    }
    return null;
  }, [data, loading]);

  return {
    loading,
    error,
    projects,
    hasNextPage,
    loadMore: loadMoreProjects,
  };
};

export default usePaginatedSuggestedProjects;
